import React, { useState } from 'react';
import {
  AlertStatus,
  AlertStatusLabels,
  AlertPatch,
  AlertBulkPatchRequest
} from '@techgitter/contracts';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { bulkPatchAlerts } from '../../services/alertsService';

interface BulkActionBarProps {
  selectedCount: number;
  selectedIds: string[];
  onClearSelection: () => void;
  onUpdate: () => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  selectedIds,
  onClearSelection,
  onUpdate
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<AlertStatus | undefined>(undefined);
  const [bulkActive, setBulkActive] = useState<boolean | undefined>(undefined);
  const [bulkNotes, setBulkNotes] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  const handleOpenDialog = () => {
    setBulkStatus(undefined);
    setBulkActive(undefined);
    setBulkNotes('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleBulkUpdate = async () => {
    const patch: AlertPatch = {};

    if (bulkStatus !== undefined) {
      patch.status = bulkStatus;
    }
    if (bulkActive !== undefined) {
      patch.isActive = bulkActive;
    }
    if (bulkNotes.trim()) {
      patch.notes = bulkNotes;
    }

    if (Object.keys(patch).length === 0) {
      alert('Please select at least one field to update');
      return;
    }

    setProcessing(true);
    try {
      const request: AlertBulkPatchRequest = {
        ids: selectedIds,
        patch
      };
      
      const result = await bulkPatchAlerts(request);
      
      if (result.failed.length > 0) {
        alert(`Bulk update completed with ${result.updated} successes and ${result.failed.length} failures`);
      }
      
      onUpdate();
      onClearSelection();
      handleCloseDialog();
    } catch (error) {
      alert(`Failed to perform bulk update: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          boxShadow: 3,
          zIndex: 1000
        }}
      >
        <Typography variant="body1">
          {selectedCount} alert{selectedCount !== 1 ? 's' : ''} selected
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
        >
          Bulk Update
        </Button>
        <Button
          variant="outlined"
          onClick={onClearSelection}
        >
          Clear Selection
        </Button>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk Update {selectedCount} Alert{selectedCount !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status (optional)</InputLabel>
              <Select<string>
                value={bulkStatus !== undefined ? String(bulkStatus) : ''}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === '') {
                    setBulkStatus(undefined);
                  } else {
                    setBulkStatus(Number(value) as AlertStatus);
                  }
                }}
                label="Status (optional)"
              >
                <MenuItem value="">No Change</MenuItem>
                <MenuItem value={String(AlertStatus.New)}>{AlertStatusLabels[AlertStatus.New]}</MenuItem>
                <MenuItem value={String(AlertStatus.Acknowledged)}>{AlertStatusLabels[AlertStatus.Acknowledged]}</MenuItem>
                <MenuItem value={String(AlertStatus.Resolved)}>{AlertStatusLabels[AlertStatus.Resolved]}</MenuItem>
                <MenuItem value={String(AlertStatus.NoActionRequired)}>{AlertStatusLabels[AlertStatus.NoActionRequired]}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Active Status (optional)</InputLabel>
              <Select<string>
                value={bulkActive === undefined ? '' : bulkActive.toString()}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === '') {
                    setBulkActive(undefined);
                  } else {
                    setBulkActive(value === 'true');
                  }
                }}
                label="Active Status (optional)"
              >
                <MenuItem value="">No Change</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Notes (optional)"
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Will be applied to all selected alerts"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkUpdate} 
            variant="contained" 
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
